import { useEffect, useMemo, useState } from "react";
import api from "api/axiosInstance";
import styles from "../AdminPanel.module.scss";

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

function parsePhones(input: string): string[] {
  const t = (input || "").trim();
  if (!t) return [];

  try {
    const parsed = JSON.parse(t);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((x) => typeof x === "string")
        .map((x) => x.trim())
        .filter(Boolean);
    }
  } catch {}

  return t
    .split(/[\n,]/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

function phonesToDetail(phones: string[]) {
  return JSON.stringify(phones.map((p) => p.trim()).filter(Boolean));
}

export default function ContactsMapSettings() {
  const [rows, setRows] = useState<ClinicInfoRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Поля формы
  const [phonesText, setPhonesText] = useState("+7 (915) 512-03-03");
  const [address, setAddress] = useState("Курск, ул. Орловская, 1А");
  const [hours, setHours] = useState("Пн–Сб 9:00–20:00, Вс выходной");

  const [mapTitle, setMapTitle] = useState("Ю-ДЕНТ");
  const [lat, setLat] = useState("51.748771");
  const [lng, setLng] = useState("36.136982");
  const [zoom, setZoom] = useState("16");
  const [mapLink, setMapLink] = useState("");

  // Футер
  const [footerAbout, setFooterAbout] = useState("");

  // Соцсети
  const [vkLink, setVkLink] = useState("");
  const [maxLink, setMaxLink] = useState("");

  // Онлайн-запись
  const [dikidiLink, setDikidiLink] = useState("");

  const byKey = useMemo(() => {
    const m = new Map<string, ClinicInfoRow>();
    rows.forEach((r) => m.set(r.name, r));
    return m;
  }, [rows]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<ClinicInfoRow[]>("/clinic-info");
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Подтягиваем значения из БД в форму
  useEffect(() => {
    const phonesDetail =
      byKey.get("contacts.phones")?.detail ||
      byKey.get("contacts.phone")?.detail ||
      "";

    const addr = byKey.get("contacts.address")?.detail || "";
    const hrs = byKey.get("contacts.hours")?.detail || "";

    const t = byKey.get("contacts.map.title")?.detail || "";
    const la = byKey.get("contacts.map.lat")?.detail || "";
    const ln = byKey.get("contacts.map.lng")?.detail || "";
    const z = byKey.get("contacts.map.zoom")?.detail || "";
    const link = byKey.get("contacts.mapLink")?.detail || "";

    const aboutText = byKey.get("footer.about")?.detail || "";

    const vk = byKey.get("contacts.social.vk")?.detail || "";
    const max = byKey.get("contacts.social.max")?.detail || "";
    const dikidi = byKey.get("contacts.dikidiLink")?.detail || "";

    if (phonesDetail) setPhonesText(parsePhones(phonesDetail).join(", "));
    if (addr) setAddress(addr);
    if (hrs) setHours(hrs);

    if (t) setMapTitle(t);
    if (la) setLat(la);
    if (ln) setLng(ln);
    if (z) setZoom(z);

    // опциональные поля обновляем всегда, чтобы очищение из БД тоже отражалось в форме
    setMapLink(link);
    setFooterAbout(aboutText);
    setVkLink(vk);
    setMaxLink(max);
    setDikidiLink(dikidi);
  }, [byKey]);

  // Надёжный upsert: если ключ есть — delete + create (без PATCH)
  const upsert = async (name: string, detail: string) => {
    const existing = byKey.get(name);
    if (existing) {
      await api.delete(`/clinic-info/${existing.id}`);
    }
    await api.post("/clinic-info", { name, detail });
  };

  const removeByName = async (name: string) => {
    const existing = byKey.get(name);
    if (existing) {
      await api.delete(`/clinic-info/${existing.id}`);
    }
  };

  const upsertOptional = async (name: string, detail: string) => {
    const value = detail.trim();

    if (!value) {
      await removeByName(name);
      return;
    }

    await upsert(name, value);
  };

  const saveAll = async () => {
    setLoading(true);
    try {
      const phones = parsePhones(phonesText);

      await upsert("contacts.phones", phonesToDetail(phones));
      await upsert("contacts.address", address.trim());
      await upsert("contacts.hours", hours.trim());

      await upsert("contacts.map.title", mapTitle.trim());
      await upsert("contacts.map.lat", lat.trim());
      await upsert("contacts.map.lng", lng.trim());
      await upsert("contacts.map.zoom", zoom.trim());

      await upsertOptional("contacts.mapLink", mapLink);
      await upsertOptional("footer.about", footerAbout);

      await upsertOptional("contacts.social.vk", vkLink);
      await upsertOptional("contacts.social.max", maxLink);
      await upsertOptional("contacts.dikidiLink", dikidiLink);

      await load();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.form_block}>
      <h3>Контакты и карта</h3>

      <label>Телефоны (через запятую)</label>
      <input
        value={phonesText}
        onChange={(e) => setPhonesText(e.target.value)}
        placeholder="+7 (999) 111-22-33, +7 (999) 444-55-66"
      />

      <label>Адрес</label>
      <input
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="Город, улица, дом"
      />

      <label>Режим работы</label>
      <input
        value={hours}
        onChange={(e) => setHours(e.target.value)}
        placeholder="Пн–Сб 9:00–20:00"
      />

      <hr style={{ margin: "10px 0 16px" }} />

      <label>VK (ссылка)</label>
      <input
        value={vkLink}
        onChange={(e) => setVkLink(e.target.value)}
        placeholder="https://vk.com/your_page"
      />

      <label>MAX (ссылка)</label>
      <input
        value={maxLink}
        onChange={(e) => setMaxLink(e.target.value)}
        placeholder="https://max.ru/your_page"
      />

      <label>Dikidi (ссылка для онлайн-записи)</label>
      <input
        value={dikidiLink}
        onChange={(e) => setDikidiLink(e.target.value)}
        placeholder="https://dikidi.ru/..."
      />

      <hr style={{ margin: "10px 0 16px" }} />

      <label>Карта: заголовок метки</label>
      <input value={mapTitle} onChange={(e) => setMapTitle(e.target.value)} />

      <label>Карта: широта (lat)</label>
      <input value={lat} onChange={(e) => setLat(e.target.value)} />

      <label>Карта: долгота (lng)</label>
      <input value={lng} onChange={(e) => setLng(e.target.value)} />

      <label>Карта: zoom</label>
      <input value={zoom} onChange={(e) => setZoom(e.target.value)} />

      <label>Ссылка на Яндекс.Карты (опционально)</label>
      <input
        value={mapLink}
        onChange={(e) => setMapLink(e.target.value)}
        placeholder="https://yandex.ru/maps/..."
      />

      <hr style={{ margin: "10px 0 16px" }} />

      <label>Нижний колонтитул, раздел: «О клинике»</label>
      <input
        value={footerAbout}
        onChange={(e) => setFooterAbout(e.target.value)}
        placeholder="Короткий текст для блока «О клинике»"
      />

      <button className={styles.btn_primary} onClick={saveAll} disabled={loading}>
        {loading ? "Сохраняю..." : "Сохранить"}
      </button>
    </div>
  );
}
